import { useState, useEffect, useRef } from 'react';
import projectManagementImage from '../../../assets/images/quanlyduanChuyenSau.jpg';

export default function ProjectManagement() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  const capabilities = [
    {
      icon: 'ri-user-star-line',
      title: 'Quản lý theo khách hàng',
      description: 'Quản lý dự án theo từng khách hàng, hợp đồng hoặc deal cụ thể'
    },
    {
      icon: 'ri-time-line',
      title: 'Theo dõi tiến độ',
      description: 'Theo dõi tiến độ, trạng thái, nguồn lực và deadline chi tiết'
    },
    {
      icon: 'ri-links-line',
      title: 'Gắn công việc trực tiếp',
      description: 'Liên kết công việc với khách hàng, hợp đồng, báo giá và dự án'
    },
    {
      icon: 'ri-file-list-3-line',
      title: 'Quản lý hợp đồng',
      description: 'Theo dõi hợp đồng, báo giá và thanh toán tập trung'
    }
  ];

  const targetIndustries = [
    { icon: 'ri-lightbulb-line', name: 'Tư vấn' },
    { icon: 'ri-code-box-line', name: 'Công nghệ' },
    { icon: 'ri-service-line', name: 'Dịch vụ' },
    { icon: 'ri-store-2-line', name: 'Thương mại' }
  ];

  return (
    <section ref={sectionRef} id="solutions" className="py-12 sm:py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid md:grid-cols-2 gap-8 sm:gap-12 md:gap-16 items-center">
          <div className={`transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
          }`}>
            <div className="inline-flex items-center space-x-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-[#2a4b7c]/10 text-[#2a4b7c] text-xs sm:text-sm font-medium mb-3 sm:mb-4">
              <i className="ri-folder-chart-line"></i>
              <span>QUẢN LÝ DỰ ÁN CHUYÊN SÂU</span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
              Quản lý dự án & công việc cho mô hình B2B
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 mb-6 sm:mb-8 leading-relaxed">
              Stackway CRM tích hợp sẵn module <strong className="text-[#2a4b7c]">Quản lý Dự án – Công việc</strong>, 
              đặc biệt phù hợp với doanh nghiệp tư vấn, triển khai giải pháp, dịch vụ công nghệ
            </p>

            <div className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
              {capabilities.map((item, index) => (
                <div key={index} className="flex items-start space-x-3 sm:space-x-4 p-3 sm:p-4 rounded-xl bg-gray-50 hover:bg-[#06b6d4]/5 transition-colors">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-lg bg-gradient-to-br from-[#06b6d4] to-[#00c897] flex-shrink-0">
                    <i className={`${item.icon} text-lg sm:text-xl text-white`}></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-sm sm:text-base text-gray-900 mb-1">{item.title}</h4>
                    <p className="text-xs sm:text-sm text-gray-600">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-gray-200 pt-4 sm:pt-6">
              <p className="text-xs sm:text-sm font-semibold text-gray-700 mb-3 sm:mb-4">Phù hợp với các ngành:</p>
              <div className="flex flex-wrap gap-2 sm:gap-3">
                {targetIndustries.map((industry, index) => (
                  <div key={index} className="flex items-center space-x-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg bg-[#2ecc71]/10 text-[#2ecc71] border border-[#2ecc71]/20">
                    <i className={`${industry.icon} text-base sm:text-lg`}></i>
                    <span className="font-medium text-xs sm:text-sm">{industry.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className={`transition-all duration-1000 delay-300 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
          }`}>
            <div className="relative">
              <img
                src={projectManagementImage}
                alt="Project Management"
                className="w-full h-auto rounded-xl sm:rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-3 -right-3 sm:-bottom-6 sm:-right-6 bg-white rounded-lg sm:rounded-xl shadow-xl p-3 sm:p-4 md:p-6 border border-gray-100">
                <div className="flex items-center space-x-2 sm:space-x-4">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 flex items-center justify-center rounded-lg bg-gradient-to-br from-[#2ecc71] to-[#00c897]">
                    <i className="ri-check-double-line text-lg sm:text-xl md:text-2xl text-white"></i>
                  </div>
                  <div>
                    <div className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900">98%</div>
                    <div className="text-xs sm:text-sm text-gray-600">Hoàn thành đúng hạn</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
